import { Injectable } from '@angular/core';
import { Clipboard } from '@capacitor/clipboard';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class ClipboardFeedbackService {
  constructor(private toastService: ToastService) {}

  async copyText(text: string, successMessage: string, errorMessage: string): Promise<boolean> {
    const value = text?.trim();

    if (!value) {
      await this.toastService.showError(errorMessage, '');
      return false;
    }

    // 1) Clipboard API asíncrona. En web móvil suele fallar cuando la copia se
    //    dispara fuera de un gesto directo (p. ej. desde el setTimeout de la
    //    pulsación larga): la activación transitoria ya ha caducado y lanza
    //    NotAllowedError. Por eso NO nos quedamos aquí si falla.
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        await this.toastService.showSuccess(successMessage, '');
        return true;
      }
    } catch {
      // continuamos con los fallbacks
    }

    // 2) Fallback síncrono document.execCommand('copy'). Funciona en navegadores
    //    móviles (Chrome/Safari) donde la Clipboard API falla por el requisito de
    //    activación del usuario — origen del "Error al copiar" en web móvil (#8).
    if (this.copyWithExecCommand(value)) {
      await this.toastService.showSuccess(successMessage, '');
      return true;
    }

    // 3) Plugin de Capacitor (entorno nativo).
    try {
      await Clipboard.write({ string: value });
      await this.toastService.showSuccess(successMessage, '');
      return true;
    } catch {
      await this.toastService.showError(errorMessage, '');
      return false;
    }
  }

  /**
   * Copia mediante un textarea temporal + execCommand('copy'). Es síncrono y no
   * exige la activación transitoria de la Clipboard API, por lo que funciona en
   * web móvil cuando navigator.clipboard.writeText falla.
   */
  private copyWithExecCommand(value: string): boolean {
    try {
      if (typeof document === 'undefined' || typeof document.execCommand !== 'function') {
        return false;
      }

      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '-9999px';
      textarea.style.left = '0';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);

      const selection = document.getSelection();
      const previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, value.length);

      const copied = document.execCommand('copy');

      document.body.removeChild(textarea);

      // Restaurar la selección previa del usuario, si la había.
      if (previousRange && selection) {
        selection.removeAllRanges();
        selection.addRange(previousRange);
      }

      return copied;
    } catch {
      return false;
    }
  }
}